import { useState, type FormEvent } from "react";
import { signIn, signUp } from "../../lib/auth-client";

type Mode = "sign_in" | "sign_up";

/**
 * Minimal admin sign-in / sign-up form. v1 supports email+password only.
 * The first time you set up the project you'll sign up, then put your user.id
 * into ADMIN_USER_IDS in .env.
 */
export function SignInPanel() {
  const [mode, setMode] = useState<Mode>("sign_in");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    const result =
      mode === "sign_in"
        ? await signIn.email({ email, password })
        : await signUp.email({ email, password, name: name || email });

    setSubmitting(false);

    if (result.error) {
      const err = result.error as { message?: string; code?: string; status?: number };
      const parts = [
        err.message,
        err.code ? `(${err.code})` : null,
        err.status ? `[${err.status}]` : null,
      ].filter(Boolean);
      const fallback = mode === "sign_in" ? "Sign-in failed." : "Couldn't create account.";
      setError(parts.length > 0 ? parts.join(" ") : fallback);
      return;
    }
    // Better Auth sets a session cookie; just refresh to let useSession pick it up.
    window.location.reload();
  };

  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-6 space-y-6">
      <h1 className="font-display text-4xl text-ink-900">Nellis Forum Admin</h1>
      <p className="text-ink-700 max-w-md">
        Sign in to create and review interviews.
      </p>

      <form onSubmit={submit} noValidate className="w-full max-w-sm space-y-4">
        {mode === "sign_up" && (
          <input
            type="text"
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className={inputCls}
          />
        )}
        <input
          type="email"
          placeholder="you@example.com"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className={inputCls}
        />
        <input
          type="password"
          placeholder="Password (8+ characters)"
          required
          minLength={8}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className={inputCls}
        />
        {error && <p className="text-sm text-danger-600">{error}</p>}
        <button
          type="submit"
          disabled={submitting}
          className="w-full px-6 py-3 rounded-lg bg-accent-600 text-parchment-50 font-medium hover:bg-accent-700 disabled:opacity-50 transition-colors"
        >
          {submitting ? "…" : mode === "sign_in" ? "Sign in" : "Create account"}
        </button>
        <button
          type="button"
          onClick={() => {
            setError(null);
            setMode(mode === "sign_in" ? "sign_up" : "sign_in");
          }}
          className="w-full text-sm text-ink-500 hover:text-ink-700 transition-colors"
        >
          {mode === "sign_in" ? "Need an account? Sign up." : "Already have an account? Sign in."}
        </button>
      </form>
    </div>
  );
}

const inputCls =
  "w-full px-4 py-3 rounded-lg bg-parchment-100 border border-parchment-300 text-ink-900 placeholder:text-ink-300 focus:outline-none focus:ring-2 focus:ring-accent-600 focus:ring-offset-2 focus:ring-offset-parchment-50";
